import { observer } from "mobx-react-lite";
import React from "react";
import { useApp } from "../../../context";

import { animated, useSprings } from "react-spring";
import WaveSurfer from "wavesurfer.js";

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  makeStyles,
  Slider,
  TextField,
  Typography,
} from "@material-ui/core";
import { useNavigate } from "react-router";

import { Icon } from "@opendash/icons";
import Steps from "rc-steps";
import { useTranslation } from "react-i18next";
import { QuestionAttributes } from "../../../parse";
import { AnswerDetails } from "../../../types/AnswerDetails";

const useStyles = makeStyles((theme) => ({
  questionContainer: {
    minHeight: "50vh",
    position: "relative",
    overflow: "hidden",
  },
  question: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "auto",
    minHeight: "100%",
  },
  questionInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "auto",
    minHeight: "100%",
    overflowY: "auto",
  },
  questionContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  questionName: {
    fontSize: "22px",
    fontWeight: "bold",
  },
  questionDescription: {
    fontSize: "18px",
    marginBottom: theme.spacing(2),
  },
  questionVideo: {
    marginBottom: theme.spacing(2),
    position: "relative",
    paddingTop: "56.25%",
  },
  questionAudio: {
    width: "100%",
    marginBottom: 20,
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  audioControls: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  audioSliders: {
    marginBottom: theme.spacing(3),
    textAlign: "center",
    width: "100%",
    marginLeft: "auto",
    marginRight: "auto",
    "& .MuiTypography-body1": {
      fontSize: "14px",
      marginBottom: theme.spacing(1),
    },
    "& .MuiSlider-root": {
      marginBottom: theme.spacing(2),
    },
  },
  choiceButtons: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  choiceButton: {
    minWidth: "150px",
    padding: theme.spacing(1, 3),
  },
  questionVideoPlayer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    marginBottom: theme.spacing(2),
  },
  questionAnswers: {
    "& *": {
      fontSize: "18px !important",
    },
  },
  questionActions: {},
  stickyBar: {
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    borderTop: "1px solid " + theme.palette.divider,
    background:
      "linear-gradient(to top, " +
      theme.palette.background.paper +
      " 60%, rgba(255,255,255,0))",
  },
}));

export const AnalyticCategoryQuestions = observer(
  function AnalyticCategoryQuestions({
    questions,
  }: {
    questions: QuestionAttributes[];
  }) {
    const app = useApp();
    const navigate = useNavigate();
    const classes = useStyles();
    const { t, i18n } = useTranslation();

    const [index, setIndex] = React.useState(0);

    const [audioAnalysis, setAudioAnalysis] = React.useState<{
      [questionId: string]: AnswerDetails;
    }>({});

    const [comments, setComments] = React.useState<{
      [questionId: string]: string;
    }>({});
    const [confidence, setConfidence] = React.useState<{
      [questionId: string]: number;
    }>({});
    const [pendingAnswers, setPendingAnswers] = React.useState<
      Record<string, 1 | 2>
    >({});
    // Which analysis dimensions the user actually moved, per question.
    // Lets us tell "rated neutral" apart from "never touched".
    const [touchedDetails, setTouchedDetails] = React.useState<{
      [questionId: string]: Record<string, boolean>;
    }>({});

    const [loadedCount, setLoadedCount] = React.useState(0);

    // Audio playback state
    const [audioPlayback, setAudioPlayback] = React.useState<{
      [questionId: string]: {
        isPlaying: boolean;
        currentTime: number;
        duration: number;
      };
    }>({});

    // Wavesurfer refs
    const wavesurferRefs = React.useRef<{ [questionId: string]: WaveSurfer }>(
      {},
    );
    const containerRefs = React.useRef<{
      [questionId: string]: HTMLDivElement | null;
    }>({});

    // Refs for measuring slide heights
    const slideRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const [containerHeight, setContainerHeight] = React.useState<number>(0);

    // Cleanup wavesurfer instances on unmount
    React.useEffect(() => {
      return () => {
        Object.values(wavesurferRefs.current).forEach((ws) => {
          ws.destroy();
        });
      };
    }, []);

    // Preload all WaveSurfer instances on mount so audio is ready when the user navigates to each question
    React.useEffect(() => {
      questions.forEach((question) => {
        if (!question?.audio) return;
        if (wavesurferRefs.current[question.objectId]) return;

        const container = containerRefs.current[question.objectId];
        if (!container) return;

        const wavesurfer = WaveSurfer.create({
          container,
          waveColor: "#4fc3f7",
          progressColor: "#29b6f6",
          cursorColor: "#1976d2",
          barWidth: 2,
          height: 120,
        });

        wavesurfer.load((question.audio as any).url);
        wavesurferRefs.current[question.objectId] = wavesurfer;

        wavesurfer.on("ready", () => {
          setLoadedCount((prev) => prev + 1);
          setAudioPlayback((prev) => ({
            ...prev,
            [question.objectId]: {
              duration: wavesurfer.getDuration(),
              currentTime: 0,
              isPlaying: false,
            },
          }));
        });

        wavesurfer.on("play", () => {
          setAudioPlayback((prev) => ({
            ...prev,
            [question.objectId]: {
              ...prev[question.objectId],
              isPlaying: true,
            },
          }));
        });

        wavesurfer.on("pause", () => {
          setAudioPlayback((prev) => ({
            ...prev,
            [question.objectId]: {
              ...prev[question.objectId],
              isPlaying: false,
            },
          }));
        });

        wavesurfer.on("audioprocess", () => {
          setAudioPlayback((prev) => ({
            ...prev,
            [question.objectId]: {
              ...prev[question.objectId],
              currentTime: wavesurfer.getCurrentTime(),
            },
          }));
        });
      });
    }, []);

    const submitAnonymousResponse = (questionId: string, answer: 1 | 2) => {
      const question = questions.find((q) => q.objectId === questionId);
      if (!question) return;
      const correct = answer === (question.synthetic ? 2 : 1);

      // Only send dimensions the user actually rated — an untouched slider is
      // absent from `details` rather than reported as a neutral 0.
      const touched = touchedDetails[questionId] || {};
      const allDetails = audioAnalysis[questionId] || {};
      const details: Record<string, number> = {};
      Object.keys(allDetails).forEach((key) => {
        if (touched[key]) {
          details[key] = allDetails[key as keyof typeof allDetails] as number;
        }
      });

      app.parse
        .cc("analytic-submit-response", {
          questionId,
          answer,
          correct,
          details,
          comment: comments[questionId] || undefined,
          confidence: confidence[questionId] ?? undefined,
        })
        .catch(() => {
          // fire-and-forget — don't surface errors to the user
        });
    };

    const changeQuestion = (questionIndex: number) => {
      wavesurferRefs.current[questions[index]?.objectId]?.pause();
      setIndex(questionIndex);
      // @ts-ignore
      set((i: number) => {
        return { x: (i - questionIndex) * window.innerWidth };
      });
    };

    const confirmAnswer = (questionIndex: number) => {
      const question = questions[questionIndex];
      if (!question) return;
      const pending = pendingAnswers[question.objectId];
      if (!pending || app.benchmarks.answers[question.objectId]) return;
      submitAnonymousResponse(question.objectId, pending);
      app.benchmarks
        .updateAnswerCurrentBenchmark(
          question.objectId,
          pending,
          audioAnalysis[question.objectId] || {},
        )
        .then(() => {
          changeQuestion(questionIndex + 1);
        });
    };

    const answerResults = questions.map(
      (question) => app.benchmarks.answers[question.objectId],
    );

    const correctAnswers = questions.map((question) =>
      question.synthetic ? 2 : 1,
    );

    const categoryIsFinished = !answerResults
      .map((value) => !!value)
      .includes(false);

    const answerResult = answerResults
      .map((answer, index) => (correctAnswers[index] === answer ? 1 : 0))
      .reduce((acc: number, v: number) => acc + v, 0);

    const slides = [
      ...questions.map((question) => {
        const confirmed = !!app.benchmarks.answers[question.objectId];
        const pending = pendingAnswers[question.objectId] ?? null;
        const displayAnswer: 1 | 2 | null = confirmed
          ? (app.benchmarks.answers[question.objectId] as 1 | 2)
          : pending;

        return (
          <div className={classes.questionInner}>
            <div className={classes.questionContent}>
              {/* Audio player */}
              {question.audio && (
                <div className={classes.questionAudio}>
                  <div
                    id={`waveform-${question.objectId}`}
                    style={{ width: "100%", height: "120px" }}
                    ref={(el) => {
                      containerRefs.current[question.objectId] = el;
                    }}
                  />
                  <div
                    className={classes.audioControls}
                    style={{
                      margin: "0 auto",
                      width: "fit-content",
                      marginBottom: 10,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const wavesurfer =
                          wavesurferRefs.current[question.objectId];
                        if (wavesurfer) {
                          if (audioPlayback[question.objectId]?.isPlaying) {
                            wavesurfer.pause();
                          } else {
                            wavesurfer.play();
                          }
                        }
                      }}
                    >
                      {audioPlayback[question.objectId]?.isPlaying
                        ? "Pause"
                        : "Play"}
                    </Button>
                    {audioPlayback[question.objectId]?.currentTime > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const wavesurfer =
                            wavesurferRefs.current[question.objectId];
                          if (wavesurfer) {
                            wavesurfer.seekTo(0);
                            setAudioPlayback((prev) => ({
                              ...prev,
                              [question.objectId]: {
                                ...prev[question.objectId],
                                currentTime: 0,
                              },
                            }));
                          }
                        }}
                      >
                        Restart
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Real / Synthetic choice — selecting highlights but does not submit */}
              <Typography variant="subtitle2" gutterBottom align="center">
                {confirmed ? "Deine Antwort:" : t("select-your-answer")}
              </Typography>
              <div className={classes.choiceButtons}>
                <Button
                  className={classes.choiceButton}
                  variant={displayAnswer === 1 ? "contained" : "outlined"}
                  color={displayAnswer === 1 ? "primary" : "default"}
                  disabled={confirmed}
                  onClick={() => {
                    if (confirmed) return;
                    setPendingAnswers((prev) => ({
                      ...prev,
                      [question.objectId]: 1,
                    }));
                  }}
                >
                  {t("answer-real")}
                </Button>
                <Button
                  className={classes.choiceButton}
                  variant={displayAnswer === 2 ? "contained" : "outlined"}
                  color={displayAnswer === 2 ? "primary" : "default"}
                  disabled={confirmed}
                  onClick={() => {
                    if (confirmed) return;
                    setPendingAnswers((prev) => ({
                      ...prev,
                      [question.objectId]: 2,
                    }));
                  }}
                >
                  {t("answer-synthetic")}
                </Button>
              </div>

              <Divider style={{ width: "100%", margin: "20px 0" }} />

              {/* Analysis section — confidence + dimension sliders, locked after confirming */}
              {question.audio && (
                <div className={classes.audioSliders}>
                  <Typography variant="h6" gutterBottom>
                    {t("detailed-audio-analysis")}
                  </Typography>

                  {/* Confidence — same slider styling as the dimensions below */}
                  <Box mb={2}>
                    <Typography variant="body1">Wie sicher bist du dir?</Typography>
                    <div
                      style={{
                        display: "grid",
                        gap: "10px",
                        gridTemplateColumns: "1fr auto 1fr",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ flexGrow: 1 }}
                        align="right"
                      >
                        unsicher
                      </Typography>
                      <Slider
                        value={confidence[question.objectId] ?? 50}
                        onChange={(_, value) => {
                          if (confirmed) return;
                          setConfidence((prev) => ({
                            ...prev,
                            [question.objectId]: value as number,
                          }));
                        }}
                        disabled={confirmed}
                        min={0}
                        max={100}
                        step={5}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}%`}
                        style={{ width: 200 }}
                      />
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        align="left"
                        style={{ flexGrow: 1 }}
                      >
                        sicher
                      </Typography>
                    </div>
                  </Box>

                  {[
                    {
                      key: "overallImpression",
                      label: t("overall-impression"),
                      minLabel: t("overall-impression-min"),
                      maxLabel: t("overall-impression-max"),
                    },
                    {
                      key: "voiceTone",
                      label: t("voice-tone"),
                      minLabel: t("voice-tone-min"),
                      maxLabel: t("voice-tone-max"),
                    },
                    {
                      key: "speechMelody",
                      label: t("speech-melody"),
                      minLabel: t("speech-melody-min"),
                      maxLabel: t("speech-melody-max"),
                    },
                    {
                      key: "pronunciation",
                      label: t("pronunciation"),
                      minLabel: t("pronunciation-min"),
                      maxLabel: t("pronunciation-max"),
                    },
                    {
                      key: "emotionality",
                      label: t("emotionality"),
                      minLabel: t("emotionality-min"),
                      maxLabel: t("emotionality-max"),
                    },
                    {
                      key: "pausesFlow",
                      label: t("pauses-flow"),
                      minLabel: t("pauses-flow-min"),
                      maxLabel: t("pauses-flow-max"),
                    },
                    {
                      key: "breathing",
                      label: t("breathing"),
                      minLabel: t("breathing-min"),
                      maxLabel: t("breathing-max"),
                    },
                    {
                      key: "acoustics",
                      label: t("acoustics-spatial-sound"),
                      minLabel: t("acoustics-spatial-sound-min"),
                      maxLabel: t("acoustics-spatial-sound-max"),
                    },
                  ].map(({ key, label, minLabel, maxLabel }) => {
                    const isTouched =
                      !!touchedDetails[question.objectId]?.[key];
                    return (
                      <Box key={key} mb={2} style={{ opacity: isTouched ? 1 : 0.45 }}>
                        <Typography variant="body1">
                          {label}
                          {!isTouched && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="textSecondary"
                              style={{ marginLeft: 6, fontStyle: "italic" }}
                            >
                              nicht bewertet
                            </Typography>
                          )}
                        </Typography>
                        <div
                          style={{
                            display: "grid",
                            gap: "10px",
                            gridTemplateColumns: "1fr auto 1fr",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ flexGrow: 1 }}
                            align="right"
                          >
                            {minLabel}
                          </Typography>
                          <Slider
                            value={
                              audioAnalysis[question.objectId]?.[
                                key as keyof (typeof audioAnalysis)[string]
                              ] || 0
                            }
                            onChange={(_, value) => {
                              if (confirmed) return;
                              const newValue =
                                Math.round((value as number) * 100) / 100;
                              setAudioAnalysis((prev) => ({
                                ...prev,
                                [question.objectId]: {
                                  ...prev[question.objectId],
                                  [key]: newValue,
                                },
                              }));
                              setTouchedDetails((prev) => ({
                                ...prev,
                                [question.objectId]: {
                                  ...prev[question.objectId],
                                  [key]: true,
                                },
                              }));
                            }}
                            disabled={confirmed}
                            min={-1}
                            max={1}
                            step={0.1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => value.toFixed(2)}
                            style={{ width: 200 }}
                          />
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            align="left"
                            style={{ flexGrow: 1 }}
                          >
                            {maxLabel}
                          </Typography>
                        </div>
                      </Box>
                    );
                  })}

                  {/* Comment — free-text reasoning, at the end of the analysis */}
                  <Box
                    mt={2}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Typography variant="body1">Kommentar</Typography>
                    <TextField
                      value={comments[question.objectId] || ""}
                      onChange={(e) => {
                        if (confirmed) return;
                        setComments((prev) => ({
                          ...prev,
                          [question.objectId]: e.target.value,
                        }));
                      }}
                      disabled={confirmed}
                      variant="outlined"
                      multiline
                      minRows={4}
                      style={{ width: "100%", maxWidth: 420 }}
                      placeholder="Optional – warum hast du dich so entschieden?"
                      inputProps={{ maxLength: 1000 }}
                    />
                  </Box>
                </div>
              )}
            </div>
          </div>
        );
      }),
      <div className={classes.questionInner}>
        <div className={classes.questionContent}>
          {(() => {
            const total = questions.length;
            const correct = answerResult;
            const percent = total > 0 ? Math.round((correct / total) * 100) : 0;
            const emoji =
              percent >= 80
                ? "🏆"
                : percent >= 60
                  ? "👍"
                  : percent >= 40
                    ? "🤔"
                    : "📚";
            const message =
              percent >= 80
                ? "Ausgezeichnet! Du hast ein sehr gutes Gespür für KI-generierte Sprache."
                : percent >= 60
                  ? "Gut gemacht! Du erkennst die meisten synthetischen Stimmen."
                  : percent >= 40
                    ? "Nicht schlecht. Mit etwas Übung wirst du noch besser."
                    : "Das Erkennen von KI-Stimmen ist schwierig – du lernst mit jeder Runde.";
            return (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                style={{
                  gap: 24,
                  padding: "32px 16px",
                  maxWidth: 480,
                  margin: "0 auto",
                }}
              >
                <Typography
                  variant="h2"
                  style={{ fontSize: 64, lineHeight: 1 }}
                >
                  {emoji}
                </Typography>
                <Typography
                  variant="h5"
                  align="center"
                  style={{ fontWeight: 700 }}
                >
                  Alle Fragen beantwortet!
                </Typography>
                <Box
                  style={{
                    background: "rgba(0,0,0,0.06)",
                    borderRadius: 16,
                    padding: "24px 40px",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="h3"
                    style={{ fontWeight: 800, lineHeight: 1.1 }}
                  >
                    {correct}{" "}
                    <Typography
                      component="span"
                      variant="h5"
                      style={{ fontWeight: 400, opacity: 0.6 }}
                    >
                      / {total}
                    </Typography>
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="textSecondary"
                    style={{ marginTop: 4 }}
                  >
                    richtig erkannt
                  </Typography>
                  <Divider style={{ margin: "16px 0" }} />
                  <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {percent} %
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Trefferquote
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  align="center"
                  color="textSecondary"
                >
                  {message}
                </Typography>
                <Box
                  display="flex"
                  style={{
                    gap: 16,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {questions.map((q, qi) => {
                    const userAnswer = answerResults[qi];
                    const isCorrect = userAnswer === correctAnswers[qi];
                    return (
                      <Box
                        key={q.objectId}
                        onClick={() => changeQuestion(qi)}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isCorrect ? "#4caf50" : "#f44336",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: "pointer",
                        }}
                        title={`Frage ${qi + 1}: ${isCorrect ? "Richtig" : "Falsch"}`}
                      >
                        {qi + 1}
                      </Box>
                    );
                  })}
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Klicke auf eine Frage, um sie nochmals anzusehen.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/benchmark")}
                >
                  {t("new-game")}
                </Button>
              </Box>
            );
          })()}
        </div>
      </div>,
    ];

    const [props, set] = useSprings(slides.length, (i) => ({
      x: i * window.innerWidth,
    }));

    // Effect to calculate and update container height
    React.useEffect(() => {
      const updateHeight = () => {
        let maxHeight = 0;
        slideRefs.current.forEach((ref) => {
          if (ref) {
            const height = ref.scrollHeight;
            maxHeight = Math.max(maxHeight, height);
          }
        });
        if (maxHeight > 0) {
          setContainerHeight(maxHeight);
        }
      };

      const timer = setTimeout(updateHeight, 100);
      window.addEventListener("resize", updateHeight);

      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", updateHeight);
      };
    }, [slides.length, audioAnalysis, audioPlayback, index, pendingAnswers]);

    React.useEffect(() => {
      const timer = setTimeout(() => {
        let maxHeight = 0;
        slideRefs.current.forEach((ref) => {
          if (ref) {
            const height = ref.scrollHeight;
            maxHeight = Math.max(maxHeight, height);
          }
        });
        if (maxHeight > 0) {
          setContainerHeight(maxHeight);
        }
      }, 300);

      return () => clearTimeout(timer);
    }, [Object.keys(wavesurferRefs.current).length]);

    React.useEffect(() => {
      questions.forEach((question) => {
        const detailKeys = [
          "overallImpression",
          "voiceTone",
          "speechMelody",
          "pronunciation",
          "emotionality",
          "pausesFlow",
          "breathing",
          "acoustics",
        ];
        detailKeys.forEach((key) => {
          if (
            !audioAnalysis[question.objectId] ||
            !audioAnalysis[question.objectId][key as keyof AnswerDetails]
          ) {
            setAudioAnalysis((prev) => ({
              ...prev,
              [question.objectId]: {
                ...prev[question.objectId],
                [key]: 0.0,
              },
            }));
          }
        });
      });

      console.log("Set question audio analysis defaults", audioAnalysis);
    }, [questions]);

    const questionsWithAudio = questions.filter((q) => q.audio).length;
    const allLoaded =
      questionsWithAudio === 0 || loadedCount >= questionsWithAudio;

    return (
      <div>
        {!allLoaded && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            style={{ padding: "40px 16px", gap: 16 }}
          >
            <CircularProgress
              variant="determinate"
              value={Math.round((loadedCount / questionsWithAudio) * 100)}
            />
            <Typography variant="body2" color="textSecondary">
              Audio wird geladen... {loadedCount} / {questionsWithAudio}
            </Typography>
          </Box>
        )}
        {allLoaded && (
          <nav>
            <Steps current={index}>
              {questions.map((question, questionIndex) => (
                <Steps.Step
                  key={questionIndex + 1}
                  title={"Frage " + (questionIndex + 1)}
                  icon={
                    !!app.benchmarks.answers[question.objectId] ? (
                      <Icon icon="fa:check" />
                    ) : (
                      <Icon icon="fa:question-circle" />
                    )
                  }
                  onClick={() => {
                    changeQuestion(questionIndex);
                  }}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </Steps>
          </nav>
        )}

        <div
          className={classes.questionContainer}
          style={{
            height: allLoaded ? containerHeight || "auto" : 1,
            visibility: allLoaded ? "visible" : "hidden",
          }}
        >
          {slides.map((slide, slideIndex) => {
            const { x } = props[slideIndex];

            return (
              <animated.div
                key={slideIndex}
                ref={(el) => {
                  slideRefs.current[slideIndex] = el;
                }}
                className={classes.question}
                style={{
                  transform: x.interpolate((x) => `translate3d(${x}px,0,0)`),
                }}
                children={slide}
              />
            );
          })}
        </div>

        {/* Sticky action bar — always reachable, driven by the current question */}
        {allLoaded &&
          index < questions.length &&
          (() => {
            const q = questions[index];
            if (!q) return null;
            const confirmed = !!app.benchmarks.answers[q.objectId];
            const pending = pendingAnswers[q.objectId] ?? null;

            // Not yet confirmed but a choice is picked → confirm & advance.
            if (!confirmed && pending !== null) {
              return (
                <div className={classes.stickyBar}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => confirmAnswer(index)}
                  >
                    {index < questions.length - 1
                      ? "Bestätigen & Weiter"
                      : "Bestätigen & Abschließen"}
                  </Button>
                </div>
              );
            }

            // Already finished and revisiting a question → offer a way back to the overview.
            if (categoryIsFinished) {
              return (
                <div className={classes.stickyBar}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<Icon icon="fa:arrow-left" />}
                    onClick={() => changeQuestion(questions.length)}
                  >
                    Zurück zur Übersicht
                  </Button>
                </div>
              );
            }

            return null;
          })()}
      </div>
    );
  },
);
